/**
 * L'accueil du chatteur — son premier passage dans le cockpit.
 *
 * Kayla, 07-10-2026 : à son premier accès, le chatteur associe son Discord,
 * peut poser son adresse crypto, choisit sa photo et son pseudo (« c'est ce
 * qui va être affiché dans les conversations »), et règle son espace de
 * travail — thème, fond d'écran, taille. Un bouton confirme.
 *
 * Deux temps, deux formes :
 *   1. AVANT Discord — une carte au milieu (Discord, puis l'adresse de
 *      paiement, facultative). Le cockpit reste fermé : c'est la porte.
 *   2. APRÈS Discord — le VRAI cockpit, et un panneau étroit à droite
 *      (`PanneauLateral`), comme le « Preview Theme » de Discord — photo,
 *      pseudo, thème, fond, taille ; PAS l'adresse de paiement (Kayla :
 *      « c'est pas dans cet onglet-là qu'on met l'adresse de paiement »). Pas de
 *      modale, pas de miniature : l'aperçu, c'est son cockpit, qui change au
 *      clic (Kayla, même jour : « je voulais pas de modal. Regarde Discord
 *      […] c'est juste un petit truc à droite »). C'est `MemberProfileGate`
 *      qui rend le cockpit derrière.
 *
 * Ce fichier ne parle PAS à Convex : il reçoit l'état et les gestes en props
 * (cf `MemberProfileCompletion`), pour être monté tel quel par le harnais
 * `accueil-dev.html`.
 *
 * Dessin (coque, 01-11-2026) : la porte est un écran plein cadre — un titre
 * Almarena, une carte de verre par étape (`Carte`), le pas fait en pastille verte
 * (`Badge`) ; le panneau garde le verre du `PanneauLateral`, la photo et le pseudo
 * dans les champs de la librairie, le pseudo CHOISI en carte à la marque.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";

import { ReglagesDuTheme, SectionLabel } from "@/shell/AtelierDuTheme";
import { PanneauLateral } from "@/shell/PanneauLateral";
import { ChatAvatar } from "@dozka/ui";
import { Badge, Button, Carte, Champ, EnTeteDePanneau } from "@/shared/ui";
import {
  Check,
  DiscordLogo,
  Image as ImageIcon,
  Loader2,
  ShieldCheck,
  Trash2,
  User,
} from "@/shared/ui/icons";
import { UsdcLogo } from "@dozka/ui";

import { ENTETE_ECRAN } from "../enteteEcran";

export interface AccueilProps {
  discord: {
    fait: boolean;
    /** Le pseudo Discord, une fois lié. */
    pseudo: string | null;
    /** La photo Discord, une fois lié (`null` = pas de photo). */
    avatarUrl: string | null;
    /** Le corps de l'étape (bouton de liaison, invitation, erreurs). */
    contenu: ReactNode;
  };
  paiement: {
    fait: boolean;
    contenu: ReactNode;
  };
  profil: {
    /** Le nom actuel de la fiche (celui des conversations aujourd'hui). */
    nomActuel: string;
    /** La photo actuelle des conversations (`null` = avatar par défaut). */
    photoUrl: string | null;
    /** `true` = la photo actuelle a été posée par la personne. */
    photoPersonnelle: boolean;
    importerPhoto: (fichier: File) => Promise<void>;
    prendrePhotoDiscord: () => Promise<void>;
    retirerPhoto: () => Promise<void>;
  };
  /** Enregistre le pseudo choisi et ferme l'accueil. */
  entrer: (pseudo: string) => Promise<void>;
  seDeconnecter: () => void;
}

export function AccueilDuChatteur(props: AccueilProps) {
  return props.discord.fait ? <PanneauDAccueil {...props} /> : <PorteDiscord {...props} />;
}

/* --------------------------------------------------------------------------- */
/* 1. La porte : Discord                                                         */
/* --------------------------------------------------------------------------- */

function PorteDiscord({ discord, paiement, seDeconnecter }: AccueilProps) {
  return (
    <div className="flex h-full overflow-y-auto bg-canvas px-4 py-10 text-fg">
      <div className="m-auto flex w-full max-w-[520px] flex-col gap-4">
        <EnTeteDePanneau
          visuel={<ShieldCheck />}
          titre="Bienvenue dans le cockpit"
          sousTitre="Connecte ton Discord pour entrer. Ensuite, tu choisis ta photo, ton pseudo et tu installes ton espace."
          className={`${ENTETE_ECRAN} mb-2 gap-3`}
        />

        <Etape numero={1} icone={<DiscordLogo weight="fill" className="h-5 w-5" />} titre="Connecter mon Discord">
          {discord.contenu}
        </Etape>
        <Etape
          numero={2}
          icone={<UsdcLogo className="h-5 w-5" />}
          titre="Mon adresse de paiement — facultatif"
          fait={paiement.fait}
        >
          {paiement.contenu}
        </Etape>

        <Button variant="ghost" size="lg" onClick={seDeconnecter} className="self-center">
          Se déconnecter
        </Button>
      </div>
    </div>
  );
}

function Etape({
  numero,
  icone,
  titre,
  fait = false,
  children,
}: {
  numero: number;
  icone: ReactNode;
  titre: string;
  fait?: boolean;
  children: ReactNode;
}) {
  return (
    <Carte remplissage="lg">
      <div className="mb-4 flex items-center gap-3">
        <Badge ton={fait ? "vert" : "gris"} size="md">
          {fait ? <Check className="h-3 w-3" aria-hidden /> : numero}
        </Badge>
        <span className="flex min-w-0 items-center gap-2 text-[15px] font-semibold">
          {icone}
          <span className="min-w-0">{titre}</span>
        </span>
      </div>
      {fait ? <p className="text-[13px] text-muted">Adresse enregistrée.</p> : children}
    </Carte>
  );
}

/* --------------------------------------------------------------------------- */
/* 2. Le panneau à droite, sur le vrai cockpit                                   */
/* --------------------------------------------------------------------------- */

function PanneauDAccueil({ discord, profil, entrer }: AccueilProps) {
  // Le pseudo : celui de Discord par défaut — c'est celui sous lequel
  // l'équipe le connaît déjà sur le serveur.
  const [choix, setChoix] = useState<"discord" | "libre">(discord.pseudo ? "discord" : "libre");
  const [pseudoLibre, setPseudoLibre] = useState(profil.nomActuel);
  const pseudoDiscordRef = useRef(discord.pseudo);
  useEffect(() => {
    if (discord.pseudo && !pseudoDiscordRef.current) setChoix("discord");
    pseudoDiscordRef.current = discord.pseudo;
  }, [discord.pseudo]);
  const pseudo = (choix === "discord" && discord.pseudo ? discord.pseudo : pseudoLibre).trim();

  const [entree, setEntree] = useState<
    { etat: "repos" | "envoi" } | { etat: "erreur"; message: string }
  >({ etat: "repos" });
  async function confirmer() {
    if (entree.etat === "envoi") return;
    setEntree({ etat: "envoi" });
    try {
      await entrer(pseudo);
    } catch (err) {
      setEntree({ etat: "erreur", message: err instanceof Error ? err.message : String(err) });
    }
  }

  return (
    <PanneauLateral
      titre="Installe-toi 👋"
      pied={
        <>
          {entree.etat === "erreur" && (
            <p className="mb-2 text-[12.5px] text-erreur">{entree.message}</p>
          )}
          {!pseudo && (
            <p className="mb-2 text-[12.5px] text-muted">Choisis ton pseudo pour entrer.</p>
          )}
          <Button
            variant="accent"
            size="xl"
            className="w-full"
            disabled={!pseudo || entree.etat === "envoi"}
            onClick={() => void confirmer()}
            icon={
              entree.etat === "envoi" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )
            }
          >
            C'est bon, j'y vais
          </Button>
        </>
      }
    >
      <p className="mb-5 text-[13px] leading-relaxed text-muted">
        Ton cockpit est juste à côté : tout ce que tu règles ici s'y voit tout de suite. Tu
        pourras le changer quand tu veux (la palette, en bas à gauche).
      </p>

      <section className="mb-6 flex flex-col gap-3">
        <SectionLabel>Ma photo et mon pseudo</SectionLabel>
        <EtapeProfil
          profil={profil}
          discordPseudo={discord.pseudo}
          discordAvatarUrl={discord.avatarUrl}
          choix={choix}
          setChoix={setChoix}
          pseudoLibre={pseudoLibre}
          setPseudoLibre={setPseudoLibre}
          pseudo={pseudo}
        />
      </section>

      <ReglagesDuTheme />

    </PanneauLateral>
  );
}

export function EtapeProfil({
  profil,
  discordPseudo,
  discordAvatarUrl,
  choix,
  setChoix,
  pseudoLibre,
  setPseudoLibre,
  pseudo,
}: {
  profil: AccueilProps["profil"];
  discordPseudo: string | null;
  discordAvatarUrl: string | null;
  choix: "discord" | "libre";
  setChoix: (c: "discord" | "libre") => void;
  pseudoLibre: string;
  setPseudoLibre: (s: string) => void;
  pseudo: string;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [photo, setPhoto] = useState<
    { etat: "repos" | "envoi" } | { etat: "erreur"; message: string }
  >({ etat: "repos" });

  async function geste(f: () => Promise<void>) {
    if (photo.etat === "envoi") return;
    setPhoto({ etat: "envoi" });
    try {
      await f();
      setPhoto({ etat: "repos" });
    } catch (err) {
      setPhoto({ etat: "erreur", message: err instanceof Error ? err.message : String(err) });
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-[13px] leading-relaxed text-muted">
        C'est ce que l'équipe voit à côté de chacun de tes messages, dans les conversations.
      </p>

      {/* Photo */}
      <div className="flex items-center gap-4">
        <ChatAvatar src={profil.photoUrl} seed={pseudo || "toi"} size={72} />
        <div className="flex min-w-0 flex-col items-start gap-1.5">
          <Button
            variant="secondary"
            size="lg"
            disabled={photo.etat === "envoi"}
            onClick={() => inputRef.current?.click()}
            icon={
              photo.etat === "envoi" ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <ImageIcon className="h-3.5 w-3.5" />
              )
            }
          >
            Importer une photo
          </Button>
          {discordAvatarUrl && (
            <Button
              variant="ghost"
              size="md"
              disabled={photo.etat === "envoi"}
              onClick={() => void geste(profil.prendrePhotoDiscord)}
              icon={
                <DiscordLogo weight="fill" className="h-3.5 w-3.5" />
              }
            >
              Prendre ma photo Discord
            </Button>
          )}
          {profil.photoPersonnelle && (
            <Button
              variant="ghost"
              size="md"
              disabled={photo.etat === "envoi"}
              onClick={() => void geste(profil.retirerPhoto)}
              icon={<Trash2 className="h-3.5 w-3.5" />}
            >
              Retirer
            </Button>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (f) void geste(() => profil.importerPhoto(f));
            }}
          />
        </div>
      </div>
      {photo.etat === "erreur" && <p className="text-[12px] text-erreur">{photo.message}</p>}

      {/* Pseudo */}
      <div className="flex flex-col gap-2.5">
        {discordPseudo && (
          <Carte
            remplissage="sm"
            actif={choix === "discord"}
            aria-pressed={choix === "discord"}
            onClick={() => setChoix("discord")}
            className="flex items-center gap-2.5 text-[13px]"
          >
            <DiscordLogo weight="fill" className="h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1 truncate">
              Mon pseudo Discord — <span className="font-semibold">{discordPseudo}</span>
            </span>
            {choix === "discord" && <Check className="h-4 w-4 shrink-0" />}
          </Carte>
        )}
        <Champ
          type="text"
          value={pseudoLibre}
          maxLength={40}
          onFocus={() => setChoix("libre")}
          onChange={(e) => setPseudoLibre(e.target.value)}
          placeholder={discordPseudo ? "Ou un autre pseudo…" : "Ton pseudo"}
          aria-label={discordPseudo ? "Ou un autre pseudo…" : "Ton pseudo"}
          icone={<User />}
          suffixe={choix === "libre" ? <Check className="h-4 w-4" /> : undefined}
          taille="lg"
        />
      </div>
    </div>
  );
}
